import React from "react";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useNavigate } from "react-router-dom";
import useUser from "../../Core/hook/useUser";

interface IProfileProps {
  profile: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  username: string;
  staffName: string;
  userRole: string;
}

const Profile: React.FC<IProfileProps> = ({
  profile,
  onClose,
  open,
  username,
  staffName,
  userRole,
}) => {
  const navigate = useNavigate();
  // Staff-only account-menu link to the new Staff Portal profile page.
  // Mirrors the sidebar's `staffOnly` gate (Menu.tsx) so admins never
  // see a link that would bounce them out of their portal. Same
  // predicate used by homeRoute/route guards.
  const { isStaffRole, isSystemAdmin, isParentUser } = useUser();
  const showStaffPortalProfile = isStaffRole && !isSystemAdmin && !isParentUser;

  const displayName = staffName || username || "";
  const initial = (displayName || "?").charAt(0).toUpperCase();

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const logout = () => {
    localStorage.removeItem("token");
    window.location.reload();
  };

  return (
    <Menu
      anchorEl={profile}
      id="account-menu"
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: "visible",
          minWidth: 260,
          filter: "drop-shadow(0px 4px 16px rgba(0,0,0,0.24))",
          mt: 1.5,
          "& .MuiAvatar-root": {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          "&::before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {/* Identity header (non-interactive) */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main", fontSize: 18, fontWeight: 600 }}>
          {initial}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            @{username}
          </Typography>
          {userRole && (
            <Chip
              label={userRole}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 0.5, height: 20, fontSize: 11, fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>

      <Divider />

      {showStaffPortalProfile && (
        <MenuItem onClick={() => go("/staff-portal/profile")} sx={{ py: 1.2 }}>
          <ListItemIcon>
            <BadgeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
      )}

      <MenuItem onClick={() => go("/my-profile")} sx={{ py: 1.2 }}>
        <ListItemIcon>
          <PersonOutlineIcon fontSize="small" />
        </ListItemIcon>
        {showStaffPortalProfile ? "Account Settings" : "My Profile"}
      </MenuItem>

      <MenuItem onClick={() => go("/setting/reset-password")} sx={{ py: 1.2 }}>
        <ListItemIcon>
          <LockResetIcon fontSize="small" />
        </ListItemIcon>
        Reset Password
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={logout}
        sx={{ py: 1.2, color: "error.main", "& .MuiListItemIcon-root": { color: "error.main" } }}
      >
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );
};

export default Profile;
